import { t as __t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import DataAutoComplete from "../../../components/auto-complete";
import SwalAlert from "../../../components/common/alert";
import ImageGallery from "../../../components/image-view";
import BackPrevious from "../../../components/back-previous";
import ConfirmModal from "../../../components/modal";
import FileUploadGrid from "../../../components/upload-file";
import Checkbox from "../../../components/common/checkBox";


import {
    convertStringToJson,
    dateToString,
    decodeBase64,
    FormatCurrencys,
    isLa,
} from "../../../utils/commonUtil";

import {
    callGetContractByKey,
    callGetContractDetail,
    callGetPaymentByContractNumber,
} from "../../../api/contract/service";

import { callExportVehicleContractPDF } from "../../../api/export";
import { callUploadFile } from "../../../api/settings/service";
import { callPaymentDebit } from "../../../api/insurances/service";

import "./vehicle-contract-compact.css";
import PaymentPage from "./PaymentPage";

const safeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    try {
        const parsed = convertStringToJson(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const hasValue = (value) =>
    value !== null && value !== undefined && value !== "";

const InfoItem = ({ label, value, full = false, red = false }) => (
    <div className={`vc-info-item ${full ? "vc-info-item-full" : ""}`}>
        <div className="vc-info-label">{label}</div>
        <div className={`vc-info-value ${red ? "vc-info-value-red" : ""}`} title={hasValue(value) ? String(value) : "-"}>
            {hasValue(value) ? value : "-"}
        </div>
    </div>
);

const SummaryItem = ({ label, value, icon, color }) => (
    <div className="vc-summary-item">
        <div className="vc-summary-icon" style={{ background: color }}>
            <i className={icon}></i>
        </div>
        <div className="vc-summary-content">
            <span>{label}</span>
            <strong title={hasValue(value) ? String(value) : "-"}>
                {hasValue(value) ? value : "-"}
            </strong>
        </div>
    </div>
);

const Index = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const params = useParams();

    const [contractList, setContractList] = useState([]);
    const [request] = useState({
        page: 1,
        maxResult: 10,
        keyword: "",
    });

    const [contract, setContract] = useState(null);
    const [initValue, setInitValue] = useState(null);
    const [payment, setPayment] = useState(null);
    const [show, setShow] = useState(false);
    const [loadingContract, setLoadingContract] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const userInfo = useSelector((state) => state.dataLogin);

    const [requestSave, setRequestSave] = useState({
        billNumber: "",
        referenceNo2: "",
        file: null,
        pay: false,
        contractNumber: "",
    });

    const languageIsLao = isLa();

    const vehicleBrand = useMemo(() => {
        if (!contract) return "-";
        return languageIsLao
            ? contract.brandName || contract.brandNameEn
            : contract.brandNameEn || contract.brandName;
    }, [contract, languageIsLao]);

    const packageName = useMemo(() => {
        if (!contract) return "-";
        return languageIsLao
            ? contract.optionNameLa || contract.optionNameEn
            : contract.optionNameEn || contract.optionNameLa;
    }, [contract, languageIsLao]);

    const loadDataPayment = async (item) => {
        if (!item) return;

        setLoadingPayment(true);

        try {
            const response = await dispatch(
                callGetPaymentByContractNumber({ contractNumber: item.contractNumber })
            );

            if (response?.payload?.responseCode === "00000") {
                const data = response?.payload?.data || {};

                setPayment({
                    statusContract: item.status,
                    ...data,
                    payPerMonth: data.packagePrice,
                    total: data.totalAmount,
                    transactionId: data.billNumber,
                    comrate3D: data["3DayComRate"],
                });
            } else {
                setPayment(null);
            }
        } catch (error) {
            console.error("Load payment error:", error);
            setPayment(null);
        } finally {
            setLoadingPayment(false);
        }
    };

    const onSelect = async (item) => {
        if (!item) {
            setContract(null);
            setPayment(null);
            return;
        }

        setLoadingContract(true);

        try {
            const requestData = {
                contractNumber: item.contractNumber,
                customerId: item.customerId,
                insuranceType: 1,
            };

            const response = await dispatch(
                callGetContractDetail(requestData)
            );

            if (response?.payload?.responseCode !== "00000") {
                setContract(null);
                setPayment(null);
                return;
            }

            const detail = response?.payload?.data || {};

            setContract({
                ...item,
                ...detail,
                imagePath: safeArray(detail.imagePath || item.imagePath),
            });
            await loadDataPayment(item);
        } catch (error) {
            console.error("Load contract detail error:", error);

            SwalAlert.error(
                __t("uiText.text023"),
                __t("uiText.text250")
            );
        } finally {
            setLoadingContract(false);
        }
    };

    useEffect(() => {
        console.log(userInfo)
        const initializeContract = async () => {
            if (!params?.data) return;

            try {
                const decoded = decodeBase64(params.data);
                if (!decoded) return;

                const data = convertStringToJson(decoded);
                if (!data) return;

                const initialContract = {
                    ...data,
                    id: data.contractNumber,
                    label: data.fullName,
                    contractNumber: data.contractNumber,
                    customerId: data.customerId,
                };
                setInitValue(initialContract);
                await onSelect(initialContract);
            } catch (error) {
                console.error("Initialize contract error:", error);
            }
        };

        initializeContract();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.data]);

    const loadData = async (value = "") => {
        if (value.id) {
            return
        }
        const keyword = value.trim();

        if (keyword.length <= 2) {
            setContractList([]);
            return;
        }

        try {
            const response = await dispatch(
                callGetContractByKey({
                    keyword,
                    page: request.page,
                    maxResult: request.maxResult,
                    insuranceType: 2,
                })
            );

            if (response?.payload?.responseCode === "00000") {
                setContractList(response?.payload?.data?.result || []);
            } else {
                setContractList([]);
            }
        } catch (error) {
            console.error("Search contract error:", error);
            setContractList([]);
        }
    };

    const downloadPDF = async (contractNumber) => {
        try {
            const response = await callExportVehicleContractPDF({
                contract: contractNumber,
            });

            if (!response || response.size === 0) {
                SwalAlert.error(
                    __t("uiText.text023"),
                    __t("uiText.text251")
                );
                return;
            }

            const url = URL.createObjectURL(response);
            const link = document.createElement("a");

            link.href = url;
            link.download = `${contractNumber}.pdf`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download PDF error:", error);

            SwalAlert.error(
                __t("uiText.text023"),
                __t("uiText.text251")
            );
        }
    };

    const handleImageSelect = (event) => {
        const hasFile = Boolean(event?.files?.[0]);

        setRequestSave((previous) => ({
            ...previous,
            file: hasFile ? { file: event } : null,
        }));
    };

    const handleChange = (event) => {
        const { name, value, checked, type } = event.target;

        setRequestSave((previous) => ({
            ...previous,
            [name]: type === "checkbox" || name === "pay" ? checked : value,
        }));
    };

    const handleSave = async () => {
        if (!requestSave?.referenceNo2?.trim()) {
            SwalAlert.error(
                __t("uiText.text023"),
                __t("uiText.text252")
            );
            return;
        }

        if (!requestSave?.file) {
            SwalAlert.error(
                __t("uiText.text023"),
                __t("uiText.text253")
            );
            return;
        }

        SwalAlert.confirm(
            t("swal.title.pay"),
            t("swal.text.confirmPay"),
            async () => {
                try {
                    let billPath = "";

                    const uploadResponse = await dispatch(
                        callUploadFile(requestSave.file.file)
                    );

                    if (
                        uploadResponse?.payload?.responseCode !== "00000"
                    ) {
                        SwalAlert.error(
                            __t("uiText.text023"),
                            t("uploadFile.billImageUploadFailed")
                        );
                        return;
                    }

                    billPath = JSON.stringify(
                        uploadResponse?.payload?.data || []
                    );

                    const requestData = {
                        billPath,
                        pay: requestSave.pay ? 1 : 0,
                        contractNumber:
                            requestSave.contractNumber ||
                            contract?.contractNumber,
                        referenceNo2: requestSave.referenceNo2.trim(),
                    };

                    const response = await dispatch(
                        callPaymentDebit(requestData)
                    );

                    if (response?.payload?.responseCode === "00000") {
                        setShow(false);

                        setRequestSave({
                            billNumber: "",
                            referenceNo2: "",
                            file: null,
                            pay: false,
                            contractNumber:
                                requestData.contractNumber,
                        });

                        await loadDataPayment(
                            requestData.contractNumber
                        );
                    } else {
                        SwalAlert.error(
                            __t("uiText.text023"),
                            `${response?.payload?.responseCode || ""} ${response?.payload?.responseDescription || ""
                            }`
                        );
                    }
                } catch (error) {
                    console.error("Save payment error:", error);

                    SwalAlert.error(
                        __t("uiText.text023"),
                        __t("uiText.text024")
                    );
                }
            }
        );
    };

    const openPaymentModal = (value = {}) => {
        setShow(true);

        setRequestSave({
            billNumber: "",
            referenceNo2: "",
            file: null,
            pay: false,
            contractNumber: contract?.contractNumber || "",
            ...value,
        });
    };

    return (
        <div className="page-wrapper dashboard vc-page">
            <span className="header-span"></span>

            <DashboardHeader />
            <MobileMenu />
            <DashboardEmployerSidebar />

            <section className="user-dashboard vc-dashboard-section">
                <div className="dashboard-outer vc-dashboard-outer">
                    <div className="vc-page-header">
                        <div className="vc-page-title">
                            {Object.keys(params).length > 0 && (
                                <BackPrevious />
                            )}

                            <div>
                                <h4>
                                    {t(
                                        "menu.contractControl.checkVehicleContract"
                                    )}
                                </h4>

                                {contract && (
                                    <span>
                                        {t("contract.contractNumber")}:{" "}
                                        <strong>
                                            {contract.contractNumber}
                                        </strong>
                                    </span>
                                )}
                            </div>
                        </div>

                        {contract && (
                            <button
                                type="button"
                                className="vc-download-button"
                                onClick={() =>
                                    downloadPDF(
                                        contract.contractNumber
                                    )
                                }
                            >
                                <i className="pi pi-download"></i>
                                <span>{t("button.downloadPDF")}</span>
                            </button>
                        )}
                    </div>

                    <div className="vc-search-card">
                        <div className="vc-search-icon">
                            <i className="pi pi-search"></i>
                        </div>

                        <div className="vc-search-content">
                            <label>
                                {t("contract.search")}
                                <span className="text-danger"> *</span>
                            </label>

                            <DataAutoComplete
                                initialData={initValue}
                                data={contractList}
                                placeholder={t("contract.keyword")}
                                handleChange={loadData}
                                onSelect={onSelect}
                            />
                        </div>
                    </div>

                    {loadingContract ? (
                        <div className="vc-loading-state">
                            <i className="pi pi-spin pi-spinner"></i>
                            <span>{__t("uiText.text047")}</span>
                        </div>
                    ) : !contract ? (
                        <div className="vc-empty-state">
                            <div className="vc-empty-icon">
                                <i className="icon icon-car"></i>
                            </div>
                            <h4>{t("contract.search")}</h4>
                            <p>
                                {__t("uiText.text254")}</p>
                        </div>
                    ) : (
                        <div className="vc-content-grid">
                            <div className="vc-left-column">
                                <div className="vc-receive-card">
                                    <span>
                                        {t(
                                            "contracts.vehicle.companyReceiveTotal"
                                        )}
                                    </span>

                                    <strong>
                                        {FormatCurrencys(
                                            payment?.totalAmount ||
                                            0
                                        )}
                                        <small>
                                            {t("currentcy.kip")}
                                        </small>
                                    </strong>
                                </div>
                                <div className="vc-profile-card">
                                    <div className="vc-profile-main">
                                        <div className="vc-avatar">
                                            <i className="pi pi-user"></i>
                                        </div>

                                        <div className="vc-profile-text">
                                            <h3>{contract.fullName || "-"}</h3>
                                            <span>
                                                <i className="pi pi-phone"></i>
                                                {contract.phone || "-"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="vc-address">
                                        <i className="pi pi-map-marker"></i>
                                        <span>{contract.address || "-"}</span>
                                    </div>

                                    <div className="vc-profile-badges">
                                        <div>
                                            <small>
                                                {t("contract.agentCode")}
                                            </small>
                                            <strong>
                                                {contract.agentCode || "-"}
                                            </strong>
                                        </div>

                                        <div>
                                            <small>
                                                {t("contract.licensePlate")}
                                            </small>
                                            <strong>
                                                {contract.licensePlate || "-"}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="vc-card vc-contract-card">
                                    <div className="vc-card-header">
                                        <div>
                                            <i className="pi pi-file"></i>
                                            <h5>
                                                {t(
                                                    "contract.contractInfo"
                                                )}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="vc-info-list">
                                        <InfoItem
                                            label={t(
                                                "contract.contractNumber"
                                            )}
                                            value={
                                                contract.contractNumber
                                            }
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.signDate"
                                            )}
                                            value={dateToString(
                                                contract.signedDate,
                                                "DD/MM/YYYY"
                                            )}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.expiredDate"
                                            )}
                                            value={dateToString(
                                                contract.expiredDate,
                                                "DD/MM/YYYY"
                                            )}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.insurancePeriod"
                                            )}
                                            value={`${contract.amountDay || 0
                                                } ${t("title.day")}`}
                                        />
                                        <InfoItem
                                            label={t("contract.package")}
                                            value={packageName}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.packagePrice"
                                            )}
                                            value={`${FormatCurrencys(
                                                contract.packagePrice
                                            )} ${t("currentcy.kip")}`}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.status"
                                            )}
                                            red={contract.status === 0}
                                            value={t('status.' + contract.status)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="vc-center-column">
                                <div className="vc-summary-grid">
                                    <SummaryItem
                                        label={t("contract.signDate")}
                                        value={dateToString(
                                            contract.signedDate,
                                            "DD/MM/YYYY"
                                        )}
                                        icon="pi pi-calendar"
                                        color="#087a52"

                                    />
                                    <SummaryItem
                                        label={t("contract.expiredDate")}
                                        value={dateToString(
                                            contract.expiredDate,
                                            "DD/MM/YYYY"
                                        )}
                                        icon="pi pi-exclamation-triangle"
                                        color="#ef4444"

                                    />
                                    <SummaryItem
                                        label={t("contract.package")}
                                        value={packageName}
                                        icon="pi pi-wallet"
                                        color="#10b981"

                                    />
                                    <SummaryItem
                                        label={t(
                                            "contract.insurancePeriod"
                                        )}
                                        value={`${contract.amountDay || 0
                                            } ${t("title.day")}`}
                                        icon="pi pi-clock"
                                        color="#f59e0b"
                                    />
                                </div>

                                <div className="vc-card vc-vehicle-card">
                                    <div className="vc-card-header">
                                        <div>
                                            <i className="icon icon-car"></i>
                                            <h5>
                                                {t("contract.carInfo")}
                                            </h5>
                                        </div>

                                        <span className="vc-plate-badge">
                                            {contract.licensePlate || "-"}
                                        </span>
                                    </div>

                                    <div className="vc-vehicle-title">
                                        <h3>{vehicleBrand || "-"}</h3>
                                        <span>
                                            {contract.vehicleModel || "-"}
                                        </span>
                                    </div>

                                    <div className="vc-vehicle-grid">
                                        <InfoItem
                                            label={t("contract.brand")}
                                            value={vehicleBrand}
                                        />
                                        <InfoItem
                                            label={t("contract.model")}
                                            value={contract.vehicleModel}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.vehiclePower"
                                            )}
                                            value={contract.vehiclePower}
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.engineNo"
                                            )}
                                            value={contract.engineNo}
                                        />
                                        <InfoItem
                                            full
                                            label={t(
                                                "contract.vehicleIdentificationNo"
                                            )}
                                            value={
                                                contract.vehicleIdentificationNo
                                            }
                                        />
                                        <InfoItem
                                            label={t(
                                                "contract.licensePlate"
                                            )}
                                            value={contract.licensePlate}
                                        />
                                    </div>
                                </div>


                                <div className="vc-card vc-document-card">
                                    <div className="vc-card-header">
                                        <div>
                                            <i className="pi pi-images"></i>
                                            <h5>
                                                {t(
                                                    "contract.documentImageCar"
                                                )}
                                            </h5>
                                        </div>

                                        <span className="vc-image-count">
                                            {contract.imagePath?.length ||
                                                0}
                                        </span>
                                    </div>

                                    <div className="vc-document-content">
                                        {contract.imagePath?.length > 0 ? (
                                            <ImageGallery
                                                paths={
                                                    contract.imagePath
                                                }
                                            />
                                        ) : (
                                            <div className="vc-no-document">
                                                <i className="pi pi-image"></i>
                                                <span>
                                                    {__t("uiText.text093")}</span>
                                            </div>
                                        )}
                                    </div>

                                </div>

                            </div>

                            <div className="vc-right-column">
                                <div className="vc-card vc-payment-card">
                                    {loadingPayment ? (
                                        <div className="vc-loading-state">
                                            <i className="pi pi-spin pi-spinner"></i>
                                            <span>
                                                {__t("uiText.text255")}</span>
                                        </div>
                                    ) : payment ? (
                                        <PaymentPage
                                            compact
                                            FormatCurrencys={
                                                FormatCurrencys
                                            }
                                            t={t}
                                            qrCode={payment}
                                            onPay={openPaymentModal}
                                        />
                                    ) : (
                                        <div className="vc-empty-payment">
                                            <i className="pi pi-wallet"></i>
                                            <span>
                                                {__t("uiText.text256")}</span>
                                        </div>
                                    )}


                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <ConfirmModal
                show={show}
                onHide={() => setShow(false)}
                title={t("contract.vehiclePayment")}
                size="md"
                centered
                confirmType="submit"
                onConfirm={handleSave}
            >
                <div className="vc-payment-modal">
                    <div className="form-group">
                        <label>
                            {t("contract.billNumber")}
                            <span className="text-danger"> *</span>
                        </label>

                        <input
                            name="referenceNo2"
                            className="form-control-uld"
                            placeholder={t("contract.billNumber")}
                            onChange={handleChange}
                            value={requestSave.referenceNo2}
                            autoComplete="off"
                        />
                    </div>

                    <div className="form-group vc-checkbox-box">
                        <Checkbox
                            label={t("contracts.commissionPay")}
                            checked={requestSave.pay}
                            name="pay"
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            {t("contract.file")}
                            <span className="text-danger"> *</span>
                        </label>

                        <FileUploadGrid
                            multiple={false}
                            folderName="payment"
                            onChange={handleImageSelect}
                            accept="image/*,application/pdf"
                            required
                        />
                    </div>
                </div>
            </ConfirmModal>

            <CopyrightFooter />
        </div>
    );
};

export default Index;
